import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Play, Settings, Clock, AlertTriangle, X, Calendar } from 'lucide-react';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { useSite } from '../../hooks/useSite';
import { useScans } from '../../hooks/useScans';
import { ScanConfigModal } from '../scan/ScanConfigModal';
import { SiteSettingsModal } from './SiteSettingsModal';
import { ScanCard } from './ScanCard';
import { ScanProgressChart } from './ScanProgressChart';
import { ScanComparisonCard } from './ScanComparisonCard';
import { ProgressBadge } from '../ui/ProgressBadge';
import { ScheduleManager, Schedule } from '../schedule/ScheduleManager';
import { ScheduleForm, ScheduleFormData } from '../schedule/ScheduleForm';
import { LIST_SCHEDULES } from '../../graphql/queries';
import { CREATE_SCHEDULE, UPDATE_SCHEDULE, DELETE_SCHEDULE, TOGGLE_SCHEDULE } from '../../graphql/mutations';

const client = generateClient();

interface ListSchedulesResponse {
  listSchedules: { items: Schedule[] };
}

interface ScheduleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string;
  schedule?: Schedule | null;
  onSubmit: (data: ScheduleFormData) => void;
  isSubmitting: boolean;
  gscConnected?: boolean;
}

// Progress & Trends section - shows when 2+ completed scans exist
function ScanProgressSection({ scans }: { scans: import('../../types').Scan[] }) {
  const completedScans = useMemo(
    () => scans.filter((s) => s.status?.toLowerCase() === 'completed' && s.results),
    [scans]
  );

  // Need at least 2 completed scans to show meaningful comparison
  if (completedScans.length < 2) {
    return null;
  }

  // Sort by completion date to get first and latest
  const sortedScans = [...completedScans].sort(
    (a, b) => new Date(a.completedAt!).getTime() - new Date(b.completedAt!).getTime()
  );

  const firstScan = sortedScans[0];
  const latestScan = sortedScans[sortedScans.length - 1];
  const previousScan = sortedScans.length > 1 ? sortedScans[sortedScans.length - 2] : null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-heading-2 font-semibold">Progress & Trends</h2>
        <ProgressBadge firstScan={firstScan} latestScan={latestScan} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ScanProgressChart scans={completedScans} />
        </div>
        {previousScan && (
          <div>
            <ScanComparisonCard currentScan={latestScan} previousScan={previousScan} />
          </div>
        )}
      </div>
    </div>
  );
}

function ScheduleFormModal({ isOpen, onClose, siteId, schedule, onSubmit, isSubmitting, gscConnected = false }: ScheduleFormModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-primary/80"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-card shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-gray-light">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-coral/10 rounded-full flex items-center justify-center">
              <Calendar className="text-coral" size={20} />
            </div>
            <h2 className="text-heading-3 font-semibold">
              {schedule ? 'Edit Schedule' : 'Create Schedule'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-medium hover:text-dark-text rounded-card transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <ScheduleForm
            siteId={siteId}
            schedule={schedule ? {
              ...schedule,
              frequency: schedule.frequency.toLowerCase() as 'once' | 'daily' | 'weekly' | 'monthly',
              tools: ['axe', 'lighthouse'], // Default tools - will be overridden by schedule config if available
            } : undefined}
            onSubmit={onSubmit}
            onCancel={onClose}
            isSubmitting={isSubmitting}
            gscConnected={gscConnected}
          />
        </div>
      </div>
    </div>
  );
}

export function SiteDetails() {
  const { siteId } = useParams<{ siteId: string }>();
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [isSubmittingSchedule, setIsSubmittingSchedule] = useState(false);
  const { site, isLoading: siteLoading, refetch: refetchSite } = useSite(siteId!);
  const { scans, isLoading: scansLoading, refetch, removeScan } = useScans(siteId!);

  // Fetch schedules
  const fetchSchedules = useCallback(async () => {
    if (!siteId) return;

    setSchedulesLoading(true);
    try {
      const response = await client.graphql({
        query: LIST_SCHEDULES,
        variables: { siteId },
      }) as GraphQLResult<ListSchedulesResponse>;

      setSchedules(response.data?.listSchedules?.items || []);
    } catch (err) {
      console.error('Error fetching schedules:', err);
      setSchedules([]);
    } finally {
      setSchedulesLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Handler for creating a new schedule
  const handleCreateSchedule = () => {
    setEditingSchedule(null);
    setShowScheduleForm(true);
  };

  // Handler for editing a schedule
  const handleEditSchedule = (scheduleId: string) => {
    const schedule = schedules.find(s => s.scheduleId === scheduleId);
    if (schedule) {
      setEditingSchedule(schedule);
      setShowScheduleForm(true);
    }
  };

  // Handler for deleting a schedule
  const handleDeleteSchedule = async (scheduleId: string) => {
    try {
      await client.graphql({
        query: DELETE_SCHEDULE,
        variables: { scheduleId },
      });
      // Remove from local state
      setSchedules(prev => prev.filter(s => s.scheduleId !== scheduleId));
    } catch (err) {
      console.error('Error deleting schedule:', err);
    }
  };

  // Handler for toggling schedule enabled/disabled
  const handleToggleSchedule = async (scheduleId: string, enabled: boolean) => {
    try {
      await client.graphql({
        query: TOGGLE_SCHEDULE,
        variables: { input: { scheduleId, enabled } },
      });
      // Update local state
      setSchedules(prev => prev.map(s =>
        s.scheduleId === scheduleId ? { ...s, enabled } : s
      ));
    } catch (err) {
      console.error('Error toggling schedule:', err);
    }
  };

  // Handler for submitting schedule form (create or update)
  const handleScheduleFormSubmit = async (data: ScheduleFormData) => {
    setIsSubmittingSchedule(true);
    try {
      if (editingSchedule) {
        // Update existing schedule
        await client.graphql({
          query: UPDATE_SCHEDULE,
          variables: {
            input: {
              scheduleId: editingSchedule.scheduleId,
              frequency: data.frequency.toUpperCase(),
              dayOfWeek: data.dayOfWeek,
              dayOfMonth: data.dayOfMonth,
              hour: data.hour,
              minute: data.minute,
              timezone: data.timezone,
              config: {
                scanTypes: data.scanTypes.map(t => {
                  const map: Record<string, string> = {
                    accessibility: 'ACCESSIBILITY', eaa: 'EAA', seo: 'SEO',
                    geo: 'GEO', brokenLinks: 'BROKEN_LINKS', toxicLinks: 'TOXIC_LINKS',
                  };
                  return map[t] ?? t.toUpperCase();
                }),
                pageLimit: data.pageLimitOverride,
                selectedTools: data.tools?.map(t => t.toUpperCase()),
              },
            },
          },
        });
      } else {
        // Create new schedule
        await client.graphql({
          query: CREATE_SCHEDULE,
          variables: {
            input: {
              siteId,
              frequency: data.frequency.toUpperCase(),
              dayOfWeek: data.dayOfWeek,
              dayOfMonth: data.dayOfMonth,
              hour: data.hour,
              minute: data.minute,
              timezone: data.timezone,
              config: {
                scanTypes: data.scanTypes.map(t => {
                  const map: Record<string, string> = {
                    accessibility: 'ACCESSIBILITY', eaa: 'EAA', seo: 'SEO',
                    geo: 'GEO', brokenLinks: 'BROKEN_LINKS', toxicLinks: 'TOXIC_LINKS',
                  };
                  return map[t] ?? t.toUpperCase();
                }),
                pageLimit: data.pageLimitOverride,
                selectedTools: data.tools?.map(t => t.toUpperCase()),
              },
            },
          },
        });
      }
      // Refresh schedules and close modal
      await fetchSchedules();
      setShowScheduleForm(false);
      setEditingSchedule(null);
    } catch (err) {
      console.error('Error saving schedule:', err);
    } finally {
      setIsSubmittingSchedule(false);
    }
  };

  // Handler for closing the schedule form modal
  const handleCloseScheduleForm = () => {
    setShowScheduleForm(false);
    setEditingSchedule(null);
  };

  if (siteLoading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="animate-spin-slow w-8 h-8 border-2 border-coral border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!site) {
    return (
      <div className="py-8">
        <div className="container-content">
          <div className="card text-center py-12">
            <AlertTriangle size={48} className="mx-auto text-status-warning mb-4" />
            <h2 className="text-heading-3 font-semibold mb-2">Site Not Found</h2>
            <p className="text-body text-gray-medium mb-6">
              The site you're looking for doesn't exist or has been deleted.
            </p>
            <Link to="/dashboard" className="btn-primary">
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="container-content">
        {/* Back Link */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-body-sm text-gray-medium hover:text-coral mb-6"
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </Link>

        {/* Site Header */}
        <div className="section-dark rounded-card p-8 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-heading-1 font-bold text-white mb-2">
                {site.name}
              </h1>
              <a
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-body text-white/70 hover:text-coral"
              >
                {site.url}
              </a>
              {site.description && (
                <p className="text-body-sm text-white/50 mt-2">
                  {site.description}
                </p>
              )}
            </div>

          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setIsConfigModalOpen(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Play size={18} />
              New Scan
            </button>
            <button
              onClick={() => setIsSettingsModalOpen(true)}
              className="btn-secondary flex items-center gap-2 text-white border-white/30 hover:bg-white/10"
            >
              <Settings size={18} />
              Settings
            </button>
          </div>
        </div>

        {/* Scheduled Scans Section */}
        <div className="mb-8">
          <ScheduleManager
            siteId={siteId!}
            schedules={schedules}
            onCreateSchedule={handleCreateSchedule}
            onEditSchedule={handleEditSchedule}
            onDeleteSchedule={handleDeleteSchedule}
            onToggleSchedule={handleToggleSchedule}
            loading={schedulesLoading}
          />
        </div>

        {/* Progress & Trends Section */}
        <ScanProgressSection scans={scans} />

        {/* Scan History */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-heading-2 font-semibold">Scan History</h2>
          <div className="flex items-center gap-2 text-body-sm text-gray-medium">
            <Clock size={16} />
            {scans.length} scan{scans.length !== 1 ? 's' : ''}
          </div>
        </div>

        {scansLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin-slow w-8 h-8 border-2 border-coral border-t-transparent rounded-full" />
          </div>
        ) : scans.length === 0 ? (
          <div className="card text-center py-12">
            <Clock size={48} className="mx-auto text-gray-medium mb-4" />
            <h3 className="text-heading-3 font-semibold mb-2">No Scans Yet</h3>
            <p className="text-body text-gray-medium mb-6">
              Start your first scan to see accessibility results
            </p>
            <button
              onClick={() => setIsConfigModalOpen(true)}
              className="btn-primary"
            >
              <Play size={18} className="mr-2" />
              Start First Scan
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {scans.map(scan => (
              <ScanCard
                key={scan.scanId}
                scan={scan}
                onCancelled={refetch}
                onTrashed={() => { removeScan(scan.scanId); refetch(); }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Scan Config Modal */}
      <ScanConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        siteId={siteId!}
        siteUrl={site.url}
        gscConnected={site.gscSettings?.connectionStatus === 'CONNECTED'}
        onSuccess={() => {
          setIsConfigModalOpen(false);
          refetch();
        }}
      />

      {/* Site Settings Modal */}
      <SiteSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        site={site}
        onSuccess={() => {
          setIsSettingsModalOpen(false);
          refetchSite();
        }}
      />

      {/* Schedule Form Modal */}
      <ScheduleFormModal
        isOpen={showScheduleForm}
        onClose={handleCloseScheduleForm}
        siteId={siteId!}
        schedule={editingSchedule}
        onSubmit={handleScheduleFormSubmit}
        isSubmitting={isSubmittingSchedule}
        gscConnected={site.gscSettings?.connectionStatus === 'CONNECTED'}
      />
    </div>
  );
}
